import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, FileText, FolderKanban, MessageSquare, Sparkles, SquareCheck } from "lucide-react";
import type { NavItem, SearchResultKind } from "@/types";
import { ALL_NAV } from "@/router/navigation";
import { useAppStore, useConversationsStore, useSettingsStore } from "@/stores";
import { Modal, SearchBox, Kbd } from "@/components/ui";
import { searchUnified } from "@/services/search";
import type { UnifiedSearchResult } from "@/types";
import { logger } from "@/services/logger";
import { cn, debounce, isTauri } from "@/utils";
import { stripSnippetMarkers } from "@/utils/highlight";

const log = logger.scope("command-palette");

type PaletteResult =
  | { kind: "nav"; id: string; label: string; item: NavItem }
  | { kind: "conversation"; id: string; label: string; snippet?: string }
  | { kind: Exclude<SearchResultKind, "conversation" | "knowledge">; id: string; label: string; snippet?: string };

const ENTITY_ICON: Record<Exclude<SearchResultKind, "conversation">, typeof MessageSquare> = {
  knowledge: FileText,
  note: FileText,
  prompt: Sparkles,
  project: FolderKanban,
  task: SquareCheck,
};

const ENTITY_PATH: Record<Exclude<SearchResultKind, "conversation" | "knowledge">, (id: string) => string> = {
  note: (id) => `/notes?id=${id}`,
  prompt: (id) => `/prompts?id=${id}`,
  project: (id) => `/projects/${id}`,
  task: (id) => `/tasks?id=${id}`,
};

/**
 * CommandPalette
 *
 * A ⌘K overlay for keyboard-first navigation. Indexes the app's destinations
 * plus (once you start typing) conversations, notes, prompts, projects and
 * tasks via `searchUnified` — one shortcut covers "go to a page" and "find
 * anything in the current workspace" rather than a binding per entity type.
 */
export function CommandPalette() {
  const open = useAppStore((s) => s.commandPaletteOpen);
  const setOpen = useAppStore((s) => s.setCommandPaletteOpen);
  const summaries = useConversationsStore((s) => s.summaries);
  const selectConversation = useConversationsStore((s) => s.selectConversation);
  const activeWorkspaceId = useSettingsStore((s) => s.activeWorkspaceId);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [unifiedHits, setUnifiedHits] = useState<UnifiedSearchResult[]>([]);

  const titleMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? summaries.filter((s) => s.title.toLowerCase().includes(q)) : [];
  }, [query, summaries]);

  // Title matches are instant (in-memory); everything else goes through
  // SQLite FTS via `searchUnified`. The debounced function is created once
  // (a fresh one per keystroke would never actually coalesce anything).
  const debouncedSearchRef = useRef(
    debounce((q: string) => {
      searchUnified(q, activeWorkspaceId, 5)
        .then(setUnifiedHits)
        .catch((err: unknown) => log.warn("Unified search failed", err));
    }, 150),
  );

  useEffect(() => {
    if (!isTauri() || !query.trim()) {
      setUnifiedHits([]);
      return;
    }
    debouncedSearchRef.current(query);
  }, [query]);

  const results = useMemo<PaletteResult[]>(() => {
    const q = query.trim().toLowerCase();
    const navResults: PaletteResult[] = (
      q ? ALL_NAV.filter((n) => n.label.toLowerCase().includes(q)) : ALL_NAV
    ).map((item) => ({ kind: "nav", id: item.id, label: item.label, item }));

    if (!q) return navResults;

    const titleIds = new Set(titleMatches.map((s) => s.id));
    const titleResults: PaletteResult[] = titleMatches
      .slice(0, 8)
      .map((s) => ({ kind: "conversation", id: s.id, label: s.title }));
    const entityResults: PaletteResult[] = unifiedHits
      .filter((hit): hit is UnifiedSearchResult & { kind: Exclude<SearchResultKind, "knowledge"> } => hit.kind !== "knowledge")
      .filter((hit) => !(hit.kind === "conversation" && titleIds.has(hit.id)))
      .map((hit): PaletteResult => ({ kind: hit.kind, id: hit.id, label: hit.title, snippet: hit.snippet }));

    return [...navResults, ...titleResults, ...entityResults];
  }, [query, titleMatches, unifiedHits]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  function run(index: number) {
    const result = results[index];
    if (!result) return;
    if (result.kind === "nav") {
      navigate(result.item.path);
    } else if (result.kind === "conversation") {
      selectConversation(result.id);
      navigate("/chat");
    } else {
      navigate(ENTITY_PATH[result.kind](result.id));
    }
    setOpen(false);
  }

  return (
    <Modal open={open} onClose={() => setOpen(false)} size="md" hideClose>
      <div
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            run(active);
          }
        }}
      >
        <SearchBox
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Go to a page or search your workspace…"
        />
        <ul className="mt-3 flex flex-col gap-0.5" role="listbox">
          {results.length === 0 && (
            <li className="px-2 py-6 text-center text-sm text-text-tertiary">
              No results
            </li>
          )}
          {results.map((result, i) => {
            const Icon =
              result.kind === "nav"
                ? result.item.icon
                : result.kind === "conversation"
                  ? MessageSquare
                  : ENTITY_ICON[result.kind];
            const isActive = i === active;
            return (
              <li key={`${result.kind}-${result.id}`} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(i)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-base transition-colors",
                    isActive
                      ? "bg-bg-hover text-text-primary"
                      : "text-text-secondary",
                  )}
                >
                  <Icon size={15} strokeWidth={1.75} aria-hidden />
                  <span className="flex flex-1 flex-col items-start gap-0 truncate text-left">
                    <span className="truncate">{result.label}</span>
                    {result.kind !== "nav" && result.snippet && (
                      <span className="truncate text-sm text-text-tertiary">
                        {stripSnippetMarkers(result.snippet)}
                      </span>
                    )}
                  </span>
                  {isActive && (
                    <CornerDownLeft
                      size={13}
                      className="text-text-tertiary"
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 flex items-center gap-3 border-t border-border pt-2.5 text-2xs text-text-faint">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd> open
          </span>
          <span className="flex items-center gap-1">
            <Kbd>esc</Kbd> close
          </span>
        </div>
      </div>
    </Modal>
  );
}
